import { useQuietFocusReturn } from '@/hooks/useQuietFocusReturn'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import type * as React from 'react'

import { KBD_FIELD_ATTR, focusKbdField } from '@/hooks/formKeyboardNav'
import { useFormKeyboardNav } from '@/hooks/useFormKeyboardNav'
import { cn, stopSelectAllPropagation } from '@/lib/utils'
import { usePortalContainer } from './portal-container'

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	const portalContainer = usePortalContainer()
	return <DialogPrimitive.Portal data-slot='dialog-portal' container={portalContainer} {...props} />
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot='dialog-overlay'
			className={cn(
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[100] bg-overlay',
				className,
			)}
			{...props}
		/>
	)
}

function DialogContent({
	className,
	children,
	hideCloseButton,
	overlayClassName,
	onKeyDown,
	onOpenAutoFocus: onOpenAutoFocusProp,
	onCloseAutoFocus,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & { hideCloseButton?: boolean; overlayClassName?: string }) {
	const handleFormKeyboardNav = useFormKeyboardNav()
	const quietFocus = useQuietFocusReturn(onOpenAutoFocusProp, onCloseAutoFocus)

	return (
		<DialogPortal data-slot='dialog-portal'>
			<DialogOverlay className={overlayClassName} />
			<DialogPrimitive.Content
				data-slot='dialog-content'
				className={cn(
					'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-[100] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
					className,
				)}
				onOpenAutoFocus={e => {
					quietFocus.onOpenAutoFocus(e)
					if (e.defaultPrevented) {
						return
					}
					const content = e.currentTarget as HTMLElement | null
					if (!content) {
						return
					}
					const firstKbdField = content.querySelector<HTMLElement>(`[${KBD_FIELD_ATTR}]`)
					if (firstKbdField) {
						e.preventDefault()
						focusKbdField(firstKbdField)
						return
					}
					const firstInput = content.querySelector<HTMLElement>('input:not([type="hidden"]), textarea, select')
					if (firstInput) {
						e.preventDefault()
						firstInput.focus()
					}
				}}
				onCloseAutoFocus={quietFocus.onCloseAutoFocus}
				onKeyDown={e => {
					// Prevent Ctrl+A from bubbling to the table and selecting all rows
					stopSelectAllPropagation(e)
					handleFormKeyboardNav(e)
					onKeyDown?.(e)
				}}
				{...props}>
				{children}
				{!hideCloseButton && (
					<DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-(--icon-standalone)">
						<XIcon />
						<span className='sr-only'>Close</span>
					</DialogPrimitive.Close>
				)}
			</DialogPrimitive.Content>
		</DialogPortal>
	)
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='dialog-header'
			className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
			{...props}
		/>
	)
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='dialog-footer'
			className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
			{...props}
		/>
	)
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot='dialog-title'
			className={cn('text-form-modal-title leading-none font-bold', className)}
			{...props}
		/>
	)
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot='dialog-description'
			className={cn('text-muted-foreground text-body', className)}
			{...props}
		/>
	)
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
}
